<template>
  <div class="simple-stat-card" :class="cardClasses" @click="handleClick">
    <!-- Header with Title and Badge -->
    <div class="card-header">
      <h3 class="card-title">{{ config.title }}</h3>
      <span v-if="config.badge" :class="badgeClasses">{{ config.badge }}</span>
    </div>

    <!-- Main Content -->
    <div class="card-content" :class="layoutClasses">
      <div
        v-for="(metric, index) in config.metrics"
        :key="`metric-${index}`"
        class="metric-item"
        :class="getMetricClasses()"
      >
        <!-- Icon -->
        <div
          v-if="metric.icon"
          class="metric-icon"
          :class="getIconClasses(metric.color)"
        >
          <i class="material-icons">{{ metric.icon }}</i>
        </div>

        <!-- Value and Label -->
        <div class="metric-content">
          <div class="metric-value" :class="getValueClasses(metric.color)">
            {{
              formatValueWithPercentage(data[metric.key], metric.isPercentage)
            }}
          </div>
          <div class="metric-label">{{ metric.label }}</div>
        </div>

        <!-- Progress indicator for trending -->
        <div
          v-if="metric.showTrend && getTrendData(metric.key)"
          class="metric-trend"
        >
          <i
            class="material-icons trend-icon"
            :class="getTrendClasses(metric.key)"
          >
            {{ getTrendIcon(metric.key) }}
          </i>
          <span class="trend-value">{{ getTrendValue(metric.key) }}%</span>
        </div>
      </div>
    </div>

    <!-- Basic Progress Bar for Document Checklist -->
    <div v-if="config.showProgressBar" class="progress-section mt-4">
      <div class="progress-header flex justify-between items-center mb-2">
        <div class="progress-labels flex space-x-4">
          <div class="progress-label-item flex items-center">
            <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span class="text-sm text-gray-600"
              >{{ data.active || 0 }} Active</span
            >
          </div>
          <div class="progress-label-item flex items-center">
            <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span class="text-sm text-gray-600"
              >{{ data.inactive || 0 }} Inactive</span
            >
          </div>
        </div>
      </div>
      <div class="progress-bar bg-gray-200 rounded-full h-2">
        <div
          class="progress-fill bg-gradient-to-r from-green-500 to-blue-500 h-full rounded-full transition-all duration-300"
          :style="{ width: progressPercentage + '%' }"
        ></div>
      </div>
    </div>

    <!-- Detailed Progress Bar (Contacts) -->
    <div
      v-if="config.showDetailedProgress"
      class="detailed-progress-section mt-4"
    >
      <div
        class="progress-bar-container bg-gray-200 rounded-full h-3 mb-3 overflow-hidden"
      >
        <div class="flex h-full">
          <div
            v-for="(item, index) in config.progressItems"
            :key="index"
            class="transition-all duration-300"
            :class="getProgressColorClass(item.color)"
            :style="{ width: getDetailedProgressWidth(item.key) + '%' }"
          ></div>
        </div>
      </div>
      <div class="progress-items space-y-2">
        <div
          v-for="(item, index) in config.progressItems"
          :key="index"
          class="flex items-center justify-between"
        >
          <div class="flex items-center">
            <div
              class="w-3 h-3 rounded-full border-2 mr-3"
              :class="getProgressBorderClass(item.color)"
            ></div>
            <span class="text-sm text-gray-600">{{ item.label }}</span>
          </div>
          <span class="text-sm font-semibold text-gray-900">{{
            formatValueWithPercentage(data[item.key], item.isPercentage)
          }}</span>
        </div>
      </div>
    </div>

    <!-- Complex Progress Bar (Applications) -->
    <div
      v-if="config.showComplexProgress"
      class="complex-progress-section mt-4"
    >
      <div
        class="progress-bar-container bg-gray-200 rounded-full h-3 mb-3 overflow-hidden"
      >
        <div class="flex h-full">
          <div
            v-for="(item, index) in config.progressItems"
            :key="index"
            class="transition-all duration-300"
            :class="getProgressColorClass(item.color)"
            :style="{ width: getComplexProgressWidth(item.key) + '%' }"
          ></div>
        </div>
      </div>
      <div class="progress-items space-y-2">
        <div
          v-for="(item, index) in config.progressItems"
          :key="index"
          class="flex items-center"
        >
          <div
            class="w-3 h-3 rounded-full mr-3"
            :class="getProgressBgClass(item.color)"
          ></div>
          <span class="text-sm font-semibold text-gray-900 mr-2">{{
            formatValueWithPercentage(data[item.key], item.isPercentage)
          }}</span>
          <span class="text-sm text-gray-600">{{ item.label }}</span>
        </div>
      </div>
    </div>

    <!-- Module Progress Bars (Tasks, Documents, Notes) -->
    <div
      v-if="config.showModuleProgress"
      class="module-progress-section mt-4 space-y-3"
    >
      <div
        v-for="(item, index) in config.moduleItems"
        :key="index"
        class="module-item"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="text-sm text-gray-600">{{ item.label }}</span>
          <div class="flex items-center">
            <span class="text-sm font-semibold text-gray-900 mr-2">{{
              formatValueWithPercentage(data[item.key], item.isPercentage)
            }}</span>
            <i v-if="item.hasIcon" class="material-icons text-gray-400 text-sm"
              >settings</i
            >
          </div>
        </div>
        <div class="progress-bar bg-gray-200 rounded-full h-1.5">
          <div
            class="h-full rounded-full transition-all duration-300"
            :class="getProgressColorClass(item.color)"
            :style="{ width: getModuleProgressWidth(item.key) + '%' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Enhanced Usage Card Layout -->
    <div v-if="config.layout === 'usage-card'" class="usage-card-content mt-4">
      <!-- Stats Overview -->
      <div class="usage-stats-grid grid grid-cols-2 gap-4 mb-6">
        <div
          v-for="(metric, index) in config.metrics"
          :key="index"
          class="usage-stat-item"
        >
          <div class="usage-stat-header flex items-center mb-2">
            <div
              class="usage-icon w-8 h-8 rounded-lg flex items-center justify-center mr-3"
              :class="getUsageIconClass(metric.color)"
            >
              <i class="material-icons text-sm">{{ metric.icon }}</i>
            </div>
            <span class="usage-label text-sm text-gray-600">{{
              metric.label
            }}</span>
          </div>
          <div class="usage-value">
            <span
              class="text-xl font-bold"
              :class="getUsageValueClass(metric.color)"
            >
              {{ formatValue(data[metric.key]) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Usage Progress Comparison -->
      <div v-if="config.showUsageProgress" class="usage-progress-section">
        <div class="usage-progress-header mb-3">
          <h4 class="text-sm font-medium text-gray-700">Usage Distribution</h4>
        </div>

        <!-- Total Usage Bar -->
        <div
          class="total-usage-bar bg-gray-100 rounded-full h-2 mb-3 overflow-hidden"
        >
          <div class="flex h-full">
            <div
              class="bg-blue-500 transition-all duration-500"
              :style="{ width: getSentPercentage() + '%' }"
            ></div>
            <div
              class="bg-green-500 transition-all duration-500"
              :style="{ width: getReceivedPercentage() + '%' }"
            ></div>
          </div>
        </div>

        <!-- Usage Comparison -->
        <div class="usage-comparison flex justify-between items-center">
          <div class="usage-comparison-item flex items-center">
            <div class="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span class="text-xs text-gray-600">
              Sent: {{ getSentPercentage() }}%
            </span>
          </div>
          <div class="usage-comparison-item flex items-center">
            <div class="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span class="text-xs text-gray-600">
              Received: {{ getReceivedPercentage() }}%
            </span>
          </div>
        </div>

        <!-- Additional Stats -->
        <div class="usage-additional-stats mt-3 grid grid-cols-2 gap-3">
          <div class="additional-stat text-center p-2 bg-gray-50 rounded-lg">
            <div class="text-sm font-semibold text-gray-900">
              {{ getTotalUsage() }}
            </div>
            <div class="text-xs text-gray-500">Total Volume</div>
          </div>
          <div class="additional-stat text-center p-2 bg-gray-50 rounded-lg">
            <div class="text-sm font-semibold text-gray-900">
              {{ getUsageRatio() }}
            </div>
            <div class="text-xs text-gray-500">{{ getUsageTrendLabel() }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Classroom Dashboard Layout -->
    <div
      v-if="config.layout === 'classroom-dashboard'"
      class="classroom-dashboard-content mt-4"
    >
      <!-- Main Stats Grid -->
      <div class="classroom-stats-grid grid grid-cols-2 gap-4 mb-6">
        <div
          v-for="(metric, index) in config.metrics"
          :key="index"
          class="classroom-stat-item"
        >
          <div class="flex items-center mb-2">
            <div
              class="classroom-icon w-10 h-10 rounded-xl flex items-center justify-center mr-3"
              :class="getUsageIconClass(metric.color)"
            >
              <i class="material-icons">{{ metric.icon }}</i>
            </div>
            <div>
              <div
                class="text-lg font-bold"
                :class="getUsageValueClass(metric.color)"
              >
                {{ formatValue(data[metric.key]) }}
              </div>
              <div class="text-xs text-gray-600">{{ metric.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Classroom Status Breakdown -->
      <div class="classroom-status-section">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          Classroom Status
        </h4>
        <div class="classroom-status-list space-y-3">
          <div
            v-for="(item, index) in config.classroomItems"
            :key="index"
            class="classroom-status-item"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div
                  class="status-icon w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                  :class="getUsageIconClass(item.color)"
                >
                  <i class="material-icons text-sm">{{ item.icon }}</i>
                </div>
                <span class="text-sm text-gray-700">{{ item.label }}</span>
              </div>
              <div class="flex items-center">
                <span class="text-sm font-semibold text-gray-900 mr-3">
                  {{ formatValue(data[item.key]) }}
                </span>
                <div
                  class="progress-indicator w-16 bg-gray-200 rounded-full h-1.5"
                >
                  <div
                    class="h-full rounded-full transition-all duration-300"
                    :class="getProgressColorClass(item.color)"
                    :style="{ width: getClassroomProgress(item.key) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Automation Dashboard Layout -->
    <div
      v-if="config.layout === 'automation-dashboard'"
      class="automation-dashboard-content mt-4"
    >
      <!-- Main Automation Stats -->
      <div class="automation-stats-grid grid grid-cols-2 gap-4 mb-6">
        <div
          v-for="(metric, index) in config.metrics"
          :key="index"
          class="automation-stat-item"
        >
          <div class="flex items-center">
            <div
              class="automation-icon w-12 h-12 rounded-xl flex items-center justify-center mr-4"
              :class="getUsageIconClass(metric.color)"
            >
              <i class="material-icons text-lg">{{ metric.icon }}</i>
            </div>
            <div>
              <div
                class="text-xl font-bold"
                :class="getUsageValueClass(metric.color)"
              >
                {{ formatValue(data[metric.key]) }}
              </div>
              <div class="text-sm text-gray-600">{{ metric.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Automation Status -->
      <div class="automation-status-section">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          Automation Status
        </h4>
        <div class="automation-status-grid grid grid-cols-2 gap-3">
          <div
            v-for="(item, index) in config.statusItems"
            :key="index"
            class="automation-status-card"
            :class="getAutomationStatusClass(item.status)"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <i
                  class="material-icons mr-2"
                  :class="getStatusIconColor(item.color)"
                  >{{ item.icon }}</i
                >
                <span class="text-sm font-medium">{{
                  formatValue(data[item.key])
                }}</span>
              </div>
              <div
                class="status-badge"
                :class="getStatusBadgeClass(item.color)"
              >
                {{ item.status }}
              </div>
            </div>
            <div class="text-xs text-gray-600 mt-1">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Graphical Dashboard Layout -->
    <div
      v-if="config.layout === 'graphical-dashboard'"
      class="graphical-dashboard-content mt-4"
    >
      <!-- Primary Metrics with Large Icons -->
      <div class="primary-metrics-grid grid grid-cols-2 gap-4 mb-6">
        <div
          v-for="(metric, index) in config.primaryMetrics"
          :key="index"
          class="primary-metric-card bg-gradient-to-br rounded-xl p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 shadow-md"
          :class="getGradientClass(metric.color)"
          :style="{ background: getGradientStyle(metric.color) }"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="text-2xl font-bold text-white mb-1">
                {{ formatValue(data[metric.key]) }}
              </div>
              <div class="text-sm text-white text-opacity-90">
                {{ metric.label }}
              </div>
            </div>
            <div
              class="metric-icon-large w-12 h-12 bg-white bg-opacity-25 rounded-xl flex items-center justify-center shadow-lg backdrop-blur-sm"
            >
              <i
                class="material-icons text-white text-xl drop-shadow-sm"
                style="font-size: 24px; line-height: 1; font-family: 'Material Icons', sans-serif;"
                :title="metric.icon"
                >{{ metric.icon }}</i
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <!-- Donut Chart for Classroom -->
        <div
          v-if="config.showDonutChart && config.chartType === 'status'"
          class="donut-chart-section mb-6"
        >
          <h4 class="text-sm font-semibold text-gray-700 mb-3">
            Status Distribution
          </h4>
          <div class="flex items-center justify-center">
            <div class="donut-chart-container relative">
              <svg width="120" height="120" class="donut-chart">
                <circle
                  v-for="(segment, index) in getDonutSegments()"
                  :key="index"
                  :cx="60"
                  :cy="60"
                  :r="45"
                  fill="none"
                  :stroke="segment.color"
                  :stroke-width="8"
                  :stroke-dasharray="segment.dashArray"
                  :stroke-dashoffset="segment.dashOffset"
                  class="donut-segment transition-all duration-500 hover:stroke-width-10"
                  :transform="`rotate(${segment.rotation} 60 60)`"
                />
              </svg>
              <div
                class="donut-center absolute inset-0 flex items-center justify-center"
              >
                <div class="text-center">
                  <div class="text-lg font-bold text-gray-900">
                    {{ getTotalItems() }}
                  </div>
                  <div class="text-xs text-gray-500">Total</div>
                </div>
              </div>
            </div>
            <!-- Legend -->
            <div class="donut-legend ml-6 space-y-2">
              <div
                v-for="(item, index) in config.progressItems"
                :key="index"
                class="legend-item flex items-center"
              >
                <i
                  class="material-icons mr-2 text-lg"
                  :class="getStatusIconColor(item.color)"
                  style="font-size: 16px;"
                  >{{ item.icon }}</i
                >
                <span class="text-sm text-gray-600 mr-2">{{ item.label }}</span>
                <span class="text-sm font-semibold text-gray-900">{{
                  formatValue(data[item.key])
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bar Chart for Automation -->
        <div
          v-if="config.showBarChart && config.chartType === 'automation'"
          class="bar-chart-section mb-6"
        >
          <h4 class="text-sm font-semibold text-gray-700 mb-3">
            Automation Overview
          </h4>
          <div class="bar-chart-container">
            <div class="flex items-end justify-center space-x-4 h-24">
              <div
                v-for="(item, index) in config.statusItems"
                :key="index"
                class="bar-item flex flex-col items-center"
              >
                <div
                  class="bar-segment rounded-t-lg transition-all duration-500 hover:opacity-80"
                  :class="getProgressBgClass(item.color)"
                  :style="{
                    height: getBarHeight(item.key) + '%',
                    width: '32px'
                  }"
                ></div>
                <div class="bar-label text-xs text-gray-700 mt-2 text-center">
                  <i
                    class="material-icons text-sm mb-1"
                    :class="getStatusIconColor(item.color)"
                    style="font-size: 14px; display: block;"
                    >{{ item.icon }}</i
                  >
                  <div class="font-semibold text-gray-900">
                    {{ formatValue(data[item.key]) }}
                  </div>
                  <div class="text-gray-600">{{ item.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stacked Chart for Events -->
        <div
          v-if="config.showStackedChart && config.chartType === 'events'"
          class="stacked-chart-section mb-6"
        >
          <h4 class="text-sm font-semibold text-gray-700 mb-3">
            Event Status Breakdown
          </h4>
          <div class="stacked-chart-container">
            <!-- Horizontal Stacked Bar -->
            <div
              class="stacked-bar bg-gray-200 rounded-full h-4 mb-4 overflow-hidden"
            >
              <div class="flex h-full">
                <div
                  v-for="(item, index) in config.eventStatusItems"
                  :key="index"
                  class="transition-all duration-500"
                  :class="getProgressColorClass(item.color)"
                  :style="{ width: getStackedWidth(item.key) + '%' }"
                ></div>
              </div>
            </div>
            <!-- Status Cards Grid -->
            <div class="status-cards-grid grid grid-cols-3 gap-2">
              <div
                v-for="(item, index) in config.eventStatusItems"
                :key="index"
                class="status-card border rounded-lg p-3 text-center hover:shadow-md transition-all duration-300"
                :class="getStatusCardClass(item.color)"
              >
                <i
                  class="material-icons text-lg mb-1"
                  :class="getStatusIconColor(item.color)"
                  style="font-size: 18px; line-height: 1;"
                  >{{ item.icon }}</i
                >
                <div class="text-sm font-semibold text-gray-900">
                  {{ formatValue(data[item.key]) }}
                </div>
                <div class="text-xs text-gray-600">{{ item.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Events Dashboard Layout -->
    <div
      v-if="config.layout === 'events-dashboard'"
      class="events-dashboard-content mt-4"
    >
      <!-- Main Event Stats -->
      <div class="events-stats-grid grid grid-cols-2 gap-4 mb-6">
        <div
          v-for="(metric, index) in config.metrics"
          :key="index"
          class="events-stat-item"
        >
          <div class="flex items-center">
            <div
              class="events-icon w-12 h-12 rounded-xl flex items-center justify-center mr-4"
              :class="getUsageIconClass(metric.color)"
            >
              <i class="material-icons text-lg">{{ metric.icon }}</i>
            </div>
            <div>
              <div
                class="text-xl font-bold"
                :class="getUsageValueClass(metric.color)"
              >
                {{ formatValue(data[metric.key]) }}
              </div>
              <div class="text-sm text-gray-600">{{ metric.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Status Breakdown -->
      <div class="events-status-section">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">Event Status</h4>
        <div class="events-status-list space-y-2">
          <div
            v-for="(item, index) in config.eventItems"
            :key="index"
            class="events-status-item"
          >
            <div
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <div
                  class="event-status-icon w-6 h-6 rounded-full flex items-center justify-center mr-3"
                  :class="getProgressBgClass(item.color)"
                >
                  <i class="material-icons text-xs text-white">{{
                    item.icon
                  }}</i>
                </div>
                <span class="text-sm text-gray-700">{{ item.label }}</span>
              </div>
              <span class="text-sm font-semibold text-gray-900">
                {{ formatValue(data[item.key]) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer with additional info -->
    <div v-if="config.footer" class="card-footer">
      <span class="footer-text">{{ config.footer }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: "SimpleStatCard",
  props: {
    config: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    cardClasses() {
      return [
        "bg-white",
        "rounded-xl",
        "shadow-sm",
        "border",
        "border-gray-100",
        "p-6",
        "hover:shadow-md",
        "transition-all",
        "duration-300",
        "cursor-pointer",
        "hover:border-gray-200"
      ];
    },

    badgeClasses() {
      const colorMap = {
        green: "bg-emerald-100 text-emerald-700",
        blue: "bg-blue-100 text-blue-700",
        red: "bg-red-100 text-red-700",
        yellow: "bg-yellow-100 text-yellow-700",
        purple: "bg-purple-100 text-purple-700"
      };

      return [
        "inline-flex",
        "items-center",
        "px-2.5",
        "py-0.5",
        "rounded-full",
        "text-xs",
        "font-medium",
        colorMap[this.config.badgeColor] || colorMap.blue
      ];
    },

    layoutClasses() {
      const layouts = {
        horizontal: "flex items-center space-x-6",
        vertical: "space-y-4",
        grid: "grid grid-cols-2 gap-4",
        "usage-card": "usage-card-layout",
        "classroom-dashboard": "classroom-dashboard-layout",
        "automation-dashboard": "automation-dashboard-layout",
        "events-dashboard": "events-dashboard-layout"
      };
      return layouts[this.config.layout] || layouts.vertical;
    },

    progressPercentage() {
      if (!this.config.showProgressBar) return 0;
      const total = this.data.total || 1;
      const active = this.data.active || 0;
      return Math.round((active / total) * 100);
    }
  },
  methods: {
    getMetricClasses() {
      if (this.config.layout === "horizontal") {
        return "flex items-center space-x-3";
      }
      return "flex items-center space-x-3";
    },

    getIconClasses(color) {
      const colorMap = {
        blue: "bg-blue-100 text-blue-600",
        green: "bg-emerald-100 text-emerald-600",
        red: "bg-red-100 text-red-600",
        yellow: "bg-yellow-100 text-yellow-600",
        purple: "bg-purple-100 text-purple-600",
        amber: "bg-amber-100 text-amber-600",
        indigo: "bg-indigo-100 text-indigo-600",
        emerald: "bg-emerald-100 text-emerald-600"
      };

      return [
        "w-10",
        "h-10",
        "rounded-lg",
        "flex",
        "items-center",
        "justify-center",
        "flex-shrink-0",
        colorMap[color] || colorMap.blue
      ];
    },

    getValueClasses(color) {
      const colorMap = {
        blue: "text-blue-600",
        green: "text-emerald-600",
        red: "text-red-600",
        yellow: "text-yellow-600",
        purple: "text-purple-600",
        amber: "text-amber-600",
        indigo: "text-indigo-600",
        emerald: "text-emerald-600"
      };

      return [
        "text-2xl",
        "font-bold",
        "leading-none",
        colorMap[color] || "text-gray-900"
      ];
    },

    formatValue(value) {
      if (typeof value === "number") {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M";
        }
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + "K";
        }
        return value.toLocaleString();
      }
      return value || "0";
    },

    formatValueWithPercentage(value, isPercentage = false) {
      if (isPercentage) {
        return value + "%";
      }
      return this.formatValue(value);
    },

    getTrendData(key) {
      return this.data.trends && this.data.trends[key];
    },

    getTrendIcon(key) {
      const trend = this.getTrendData(key);
      if (!trend) return "";
      return trend > 0
        ? "trending_up"
        : trend < 0
        ? "trending_down"
        : "trending_flat";
    },

    getTrendClasses(key) {
      const trend = this.getTrendData(key);
      if (!trend) return "";
      return trend > 0
        ? "text-green-500"
        : trend < 0
        ? "text-red-500"
        : "text-gray-400";
    },

    getTrendValue(key) {
      const trend = this.getTrendData(key);
      return trend ? Math.abs(trend).toFixed(1) : "0";
    },

    handleClick() {
      this.$emit("card-click", {
        type: "simple",
        config: this.config,
        data: this.data
      });
    },

    // New methods for progress bars
    getProgressColorClass(color) {
      const colorMap = {
        blue: "bg-blue-500",
        green: "bg-green-500",
        red: "bg-red-500",
        yellow: "bg-yellow-500",
        purple: "bg-purple-500",
        amber: "bg-amber-500",
        emerald: "bg-emerald-500"
      };
      return colorMap[color] || colorMap.blue;
    },

    getProgressBgClass(color) {
      const colorMap = {
        blue: "bg-blue-500",
        green: "bg-green-500",
        red: "bg-red-500",
        yellow: "bg-yellow-500",
        purple: "bg-purple-500",
        amber: "bg-amber-500",
        emerald: "bg-emerald-500"
      };
      return colorMap[color] || colorMap.blue;
    },

    getProgressBorderClass(color) {
      const colorMap = {
        blue: "border-blue-500 bg-white",
        green: "border-green-500 bg-white",
        red: "border-red-500 bg-white",
        yellow: "border-yellow-500 bg-white",
        purple: "border-purple-500 bg-white",
        amber: "border-amber-500 bg-white",
        emerald: "border-emerald-500 bg-white"
      };
      return colorMap[color] || colorMap.blue;
    },

    getDetailedProgressWidth(key) {
      const total = this.data.total || 1;
      const value = this.data[key] || 0;
      return Math.round((value / total) * 100);
    },

    getComplexProgressWidth(key) {
      const total = this.data.total || 1;
      const value = this.data[key] || 0;
      return Math.round((value / total) * 100);
    },

    getModuleProgressWidth(key) {
      const total = this.data.total || 1;
      const value = this.data[key] || 0;
      return Math.round((value / total) * 100);
    },

    // Usage Card Methods
    getUsageIconClass(color) {
      const colorMap = {
        blue: "bg-blue-100 text-blue-600",
        green: "bg-green-100 text-green-600",
        purple: "bg-purple-100 text-purple-600",
        red: "bg-red-100 text-red-600",
        yellow: "bg-yellow-100 text-yellow-600"
      };
      return colorMap[color] || colorMap.blue;
    },

    getUsageValueClass(color) {
      const colorMap = {
        blue: "text-blue-600",
        green: "text-green-600",
        purple: "text-purple-600",
        red: "text-red-600",
        yellow: "text-yellow-600"
      };
      return colorMap[color] || "text-gray-900";
    },

    getSentPercentage() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;
      const total = sent + received;
      return total > 0 ? Math.round((sent / total) * 100) : 0;
    },

    getReceivedPercentage() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;
      const total = sent + received;
      return total > 0 ? Math.round((received / total) * 100) : 0;
    },

    getUsageTrend() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;

      if (sent > received) {
        return "More messages sent than received";
      } else if (received > sent) {
        return "More messages received than sent";
      } else {
        return "Balanced sending and receiving";
      }
    },

    getTotalUsage() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;
      return this.formatValue(sent + received);
    },

    getUsageRatio() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;
      if (received === 0) return "N/A";
      const ratio = (sent / received).toFixed(1);
      return `${ratio}:1`;
    },

    getUsageTrendLabel() {
      const sent = this.data.sent || 0;
      const received = this.data.received || 0;

      if (sent > received) {
        return "Send Ratio";
      } else if (received > sent) {
        return "Receive Ratio";
      } else {
        return "Balance";
      }
    },

    // Dashboard Layout Methods
    getClassroomProgress(key) {
      const total = this.data.totalSessions || 1;
      const value = this.data[key] || 0;
      return Math.round((value / total) * 100);
    },

    getAutomationStatusClass(status) {
      const statusMap = {
        active: "border-l-4 border-green-500 bg-green-50",
        inactive: "border-l-4 border-red-500 bg-red-50"
      };
      return statusMap[status] || "border-l-4 border-gray-500 bg-gray-50";
    },

    getStatusIconColor(color) {
      const colorMap = {
        green: "text-green-600",
        red: "text-red-600",
        blue: "text-blue-600",
        yellow: "text-yellow-600"
      };
      return colorMap[color] || "text-gray-600";
    },

    getStatusBadgeClass(color) {
      const colorMap = {
        green: "bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs",
        red: "bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs",
        blue: "bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs",
        yellow: "bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs"
      };
      return (
        colorMap[color] ||
        "bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
      );
    },

    // Graphical Dashboard Methods
    getGradientClass(color) {
      const gradientMap = {
        blue: "from-blue-500 to-blue-600",
        green: "from-green-500 to-green-600",
        yellow: "from-yellow-500 to-yellow-600",
        purple: "from-purple-500 to-purple-600",
        red: "from-red-500 to-red-600",
        indigo: "from-indigo-500 to-indigo-600"
      };
      return gradientMap[color] || gradientMap.blue;
    },

    getGradientStyle(color) {
      const gradientMap = {
        blue: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
        green: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
        yellow: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        purple: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
        red: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
        indigo: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
      };
      return gradientMap[color] || gradientMap.blue;
    },

    getDonutSegments() {
      if (!this.config.progressItems) return [];

      const total = this.getTotalItems();
      let currentOffset = 0;
      const circumference = 2 * Math.PI * 45; // radius = 45

      return this.config.progressItems.map(item => {
        const value = this.data[item.key] || 0;
        const percentage = total > 0 ? value / total : 0;
        const strokeLength = percentage * circumference;
        const segment = {
          color: this.getStrokeColor(item.color),
          dashArray: `${strokeLength} ${circumference}`,
          dashOffset: -currentOffset,
          rotation: -90
        };
        currentOffset += strokeLength;
        return segment;
      });
    },

    getStrokeColor(color) {
      const colorMap = {
        blue: "#3b82f6",
        green: "#10b981",
        yellow: "#f59e0b",
        red: "#ef4444",
        purple: "#8b5cf6",
        gray: "#6b7280"
      };
      return colorMap[color] || colorMap.blue;
    },

    getTotalItems() {
      if (!this.config.progressItems) return 0;
      return this.config.progressItems.reduce((total, item) => {
        return total + (this.data[item.key] || 0);
      }, 0);
    },

    getBarHeight(key) {
      if (!this.config.statusItems) return 0;
      const maxValue = Math.max(
        ...this.config.statusItems.map(item => this.data[item.key] || 0)
      );
      const value = this.data[key] || 0;
      return maxValue > 0 ? Math.round((value / maxValue) * 100) : 0;
    },

    getStackedWidth(key) {
      if (!this.config.eventStatusItems) return 0;
      const total = this.config.eventStatusItems.reduce((sum, item) => {
        return sum + (this.data[item.key] || 0);
      }, 0);
      const value = this.data[key] || 0;
      return total > 0 ? Math.round((value / total) * 100) : 0;
    },

    getStatusCardClass(color) {
      const colorMap = {
        green: "border-green-200 bg-green-50",
        yellow: "border-yellow-200 bg-yellow-50",
        gray: "border-gray-200 bg-gray-50",
        blue: "border-blue-200 bg-blue-50"
      };
      return colorMap[color] || colorMap.blue;
    }
  }
};
</script>

<style scoped>
.simple-stat-card {
  position: relative;
  overflow: hidden;
}

/* Usage Card Styles */
.usage-card-layout {
  display: block;
}

.usage-stat-item {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.usage-stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e1;
}

.usage-icon {
  transition: transform 0.3s ease;
}

.usage-stat-item:hover .usage-icon {
  transform: scale(1.1);
}

.usage-value {
  margin-top: 8px;
}

.usage-progress-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.total-usage-bar {
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.usage-comparison-item {
  background: white;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Graphical Dashboard Styles */
.graphical-dashboard-content {
  padding: 0;
}

.primary-metric-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.primary-metric-card:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.metric-icon-large {
  transition: all 0.3s ease;
}

.primary-metric-card:hover .metric-icon-large {
  transform: rotate(10deg) scale(1.1);
}

/* Donut Chart Styles */
.donut-chart-container {
  width: 120px;
  height: 120px;
}

.donut-segment {
  cursor: pointer;
}

.donut-segment:hover {
  filter: brightness(1.1);
}

.donut-center {
  pointer-events: none;
}

.donut-legend .legend-item {
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
}

.donut-legend .legend-item:hover {
  background-color: #f8fafc;
  transform: translateX(4px);
}

/* Bar Chart Styles */
.bar-chart-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.bar-item {
  position: relative;
}

.bar-segment {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.bar-segment:hover {
  transform: scaleY(1.05);
  filter: brightness(1.1);
}

.bar-label {
  transition: all 0.2s ease;
}

.bar-item:hover .bar-label {
  transform: translateY(-2px);
  color: #374151;
}

/* Stacked Chart Styles */
.stacked-chart-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
}

.stacked-bar {
  transition: all 0.3s ease;
  cursor: pointer;
}

.stacked-bar:hover {
  transform: scaleY(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.status-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.status-card:hover {
  transform: translateY(-2px);
  border-color: #3b82f6;
}

.status-card:hover .material-icons {
  transform: scale(1.2);
  color: #3b82f6 !important;
}

/* Material Icons fix */
.material-icons {
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: "liga";
  -webkit-font-smoothing: antialiased;
}

/* Animation keyframes */
@keyframes chartLoad {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.donut-chart-section,
.bar-chart-section,
.stacked-chart-section {
  animation: chartLoad 0.6s ease-out;
}

/* Dashboard Layout Styles */
.classroom-dashboard-layout,
.automation-dashboard-layout,
.events-dashboard-layout {
  display: block;
}

.classroom-stat-item,
.automation-stat-item,
.events-stat-item {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.classroom-stat-item:hover,
.automation-stat-item:hover,
.events-stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.classroom-status-item {
  background: white;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.classroom-status-item:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.automation-status-card {
  background: white;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.2s ease;
}

.automation-status-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.events-status-item {
  transition: all 0.2s ease;
}

.events-status-item:hover {
  transform: translateX(4px);
}

.progress-indicator {
  transition: all 0.3s ease;
}

.status-badge {
  font-weight: 500;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.simple-stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.simple-stat-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.metric-item {
  transition: transform 0.2s ease;
}

.metric-item:hover {
  transform: translateX(2px);
}

.metric-content {
  flex: 1;
  min-width: 0;
}

.metric-value {
  line-height: 1;
  margin-bottom: 0.25rem;
}

.metric-label {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.2;
}

.metric-trend {
  display: flex;
  align-items: center;
  space-x: 0.25rem;
  margin-left: auto;
}

.trend-icon {
  font-size: 1rem;
}

.trend-value {
  font-size: 0.75rem;
  font-weight: 500;
}

.card-footer {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.footer-text {
  font-size: 0.75rem;
  color: #9ca3af;
}

/* Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.simple-stat-card {
  animation: slideInUp 0.4s ease-out;
}
</style>
